import Flex from '@react-css/flex/dist/src/components/FlexWrapper'

import iconCheck from '../../../assets/images/icon_check_white.svg'
import { blue } from '../../../styles/colors'
import { ProgressCircle } from '../../views/progress/ProgressFloorTracker/ProgressCircle/ProgressCircle'
import { Icon } from '../Icon'

export interface MultiSelectOptionRowProps {
  name: string
  checked: boolean
  onCheck: (checked: boolean) => void
  onClickLabel?: () => void
  displayColor?: string
  percent?: number
  /** When false, omit the progress ring (e.g. report generator checklists). Default true. */
  showProgress?: boolean
  disabled?: boolean
  /** Partial selection: filled box with a dash; click selects all (onCheck(true)). */
  indeterminate?: boolean
  /**
   * Optional right-side select-all control (review tracking). When set, replaces
   * the progress ring and uses a fixed dark-blue checkbox style.
   */
  selectAllChecked?: boolean
  selectAllIndeterminate?: boolean
  onSelectAll?: (checked: boolean) => void
}

export const MultiSelectOptionRow = ({
  name,
  checked,
  onCheck,
  onClickLabel,
  displayColor = blue,
  percent = 0,
  showProgress = true,
  disabled = false,
  indeterminate = false,
  selectAllChecked,
  selectAllIndeterminate = false,
  onSelectAll,
}: MultiSelectOptionRowProps) => {
  const color = displayColor || blue
  const filled = checked || indeterminate
  const showSelectAll = typeof onSelectAll === 'function'
  const selectAllFilled = Boolean(selectAllChecked || selectAllIndeterminate)

  const onClickCheckmark = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) return
    // Indeterminate → select all; otherwise toggle.
    onCheck(indeterminate ? true : !checked)
  }

  const onClickSelectAll = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled || !onSelectAll) return
    onSelectAll(selectAllIndeterminate ? true : !selectAllChecked)
  }

  const handleLabelClick = (
    e:
      | React.MouseEvent<HTMLHeadingElement>
      | React.TouchEvent<HTMLHeadingElement>,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled || !onClickLabel) return
    onClickLabel()
  }

  return (
    <Flex
      alignItemsCenter
      justifySpaceBetween
      style={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <Flex
        alignItemsCenter
        style={{
          flex: 1,
          minWidth: 0,
          marginRight: 8,
        }}
      >
        <div
          onClick={onClickCheckmark}
          onTouchEnd={onClickCheckmark}
          role="checkbox"
          aria-checked={indeterminate ? 'mixed' : checked}
          style={{
            background: filled ? color : 'transparent',
            border: `1px solid ${color}`,
            padding: '6px',
            borderRadius: '4px',
            marginRight: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            flexShrink: 0,
            boxSizing: 'border-box',
            width: 26,
            height: 26,
          }}
        >
          {indeterminate ? (
            <span
              aria-hidden
              style={{
                display: 'block',
                width: 12,
                height: 2,
                borderRadius: 1,
                background: '#fff',
              }}
            />
          ) : (
            <Icon
              icon={iconCheck}
              size={12}
              style={{
                visibility: checked ? 'visible' : 'hidden',
              }}
            />
          )}
        </div>
        <h1
          style={{
            cursor: onClickLabel && !disabled ? 'pointer' : 'default',
            margin: 0,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.25,
            flex: 1,
            minWidth: 0,
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
          }}
          onClick={onClickLabel ? handleLabelClick : undefined}
        >
          {name}
        </h1>
      </Flex>
      {showSelectAll ? (
        <div
          onClick={onClickSelectAll}
          onTouchEnd={onClickSelectAll}
          role="checkbox"
          aria-checked={selectAllIndeterminate ? 'mixed' : !!selectAllChecked}
          aria-label={`Select all ${name}`}
          style={{
            background: selectAllFilled ? blue : 'transparent',
            border: `1px solid ${blue}`,
            padding: '6px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            flexShrink: 0,
            boxSizing: 'border-box',
            width: 26,
            height: 26,
          }}
        >
          {selectAllIndeterminate ? (
            <span
              aria-hidden
              style={{
                display: 'block',
                width: 12,
                height: 2,
                borderRadius: 1,
                background: '#fff',
              }}
            />
          ) : (
            <Icon
              icon={iconCheck}
              size={12}
              style={{
                visibility: selectAllChecked ? 'visible' : 'hidden',
              }}
            />
          )}
        </div>
      ) : showProgress ? (
        <ProgressCircle progress={percent} color={color} />
      ) : null}
    </Flex>
  )
}

/** @deprecated Use MultiSelectOptionRow — kept for existing imports. */
export const TypeSelector = MultiSelectOptionRow
